/**
 * De chat-reducer.
 *
 * Pure functie: (state, action) => nieuwe state. Nooit muteren.
 * state.session is een discriminated union op `phase`; server-canonical
 * messages en pending-messages zijn gescheiden. Dedup bij
 * SERVER_STATE_RECEIVED gebruikt text + timestamp-proximity.
 */

import { i18n } from '../i18n/nl';
import { formatUnitAddress } from '../utils/contract-format';
import type {
  AssistantMessage,
  ChatAction,
  ChatState,
  PendingMessage,
  RatingState,
  ServerMessage,
  SessionError,
} from './types';

const ASSISTANT_ECHO_WINDOW_MS = 5 * 60 * 1000; // matcht Turnstile-token TTL

export const initialState: ChatState = {
  ui: { panelOpen: false, lastReadAt: 0, pickedChannel: null, pickerStep: 'root' },
  connection: { online: navigator.onLine },
  serviceStatus: null,
  auth: 'unknown',
  identityJwt: null,
  session: { phase: 'none' },
  visitor: null,
  agentTyping: false,
  serverMessages: [],
  pendingMessages: [],
  stagedFiles: [],
  intake: null,
  assistant: null,
  assistantMessages: [],
  contracts: { status: 'idle', items: [], selectedId: null, error: null },
  aava: {
    status: 'idle',
    conversationId: null,
    error: null,
    finished: false,
    outcome: null,
    selfFix: 'idle',
  },
};

const DEDUP_WINDOW_MS = 60_000;

// =============================================================================
// Virtuele-assistent teksten + validatie
// =============================================================================

// TLD vereist 2+ karakters — sluit invoer als "a@b.c" uit, sluit aan op
// gangbare e-mailvalidatie (RFC laat 1-char TLD's toe maar in praktijk
// bestaan die niet als publiek registreerbare extensie).
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

// Bot-copy zit in src/i18n/nl.ts zodat alle UI-strings op één plek staan.
const ASSISTANT_COPY = i18n.bot;

function validateName(input: string): { ok: true; value: string } | { ok: false; error: string } {
  const trimmed = input.trim();
  if (trimmed.length < 2) return { ok: false, error: ASSISTANT_COPY.invalidNameTooShort };
  if (trimmed.length > 60) return { ok: false, error: ASSISTANT_COPY.invalidNameTooLong };
  // Vereis ten minste één letter — voorkomt waardes zoals ". ", "12", "--".
  if (!/\p{L}/u.test(trimmed)) {
    return { ok: false, error: ASSISTANT_COPY.invalidNameTooShort };
  }
  return { ok: true, value: trimmed };
}

function validateEmail(input: string): { ok: true; value: string } | { ok: false; error: string } {
  const trimmed = input.trim();
  if (!EMAIL_REGEX.test(trimmed)) return { ok: false, error: ASSISTANT_COPY.invalidEmail };
  return { ok: true, value: trimmed };
}

let assistantMsgCounter = 0;
/**
 * Mapping van `SessionError` naar leesbare copy voor de in-chat system-pill.
 * Wordt gebruikt door de SESSION_REQUEST_FAILED-reducer wanneer de bezoeker
 * midden in een bot-flow zat en zijn context behouden moet blijven.
 */
function sessionFailureCopy(reason: SessionError): string {
  switch (reason) {
    case 'network': return ASSISTANT_COPY.sessionFailedNetwork;
    case 'captcha_invalid': return ASSISTANT_COPY.sessionFailedCaptcha;
    case 'captcha_system_error': return ASSISTANT_COPY.sessionFailedCaptchaSystem;
    case 'service_unavailable': return ASSISTANT_COPY.sessionFailedUnavailable;
    case 'session_expired':
    case 'unknown':
    default:
      return ASSISTANT_COPY.sessionFailedUnknown;
  }
}

/**
 * Plak de "captcha-gelukt"-suffix achter de bestaande askCaptcha-balloon.
 * Idempotent: bij een dubbele CAPTCHA_VERIFIED dispatch (race) wordt de
 * suffix maar één keer toegevoegd. Geeft de originele lijst terug wanneer
 * er niks te ack-en valt, zodat referentie-gelijkheid behouden blijft en
 * Lit niet onnodig hertekent.
 */
function ackCaptchaBalloon(messages: AssistantMessage[]): AssistantMessage[] {
  const suffix = ASSISTANT_COPY.askCaptchaDoneSuffix;
  const prompt = ASSISTANT_COPY.askCaptcha;
  let touched = false;
  const updated = messages.map((m) => {
    if (m.author !== 'assistant' || m.text !== prompt) return m;
    touched = true;
    return { ...m, text: prompt + suffix };
  });
  return touched ? updated : messages;
}

function makeAssistantMessage(
  author: 'assistant' | 'customer' | 'system',
  text: string,
  timestamp: number = Date.now(),
  aavaMessageId?: number,
): AssistantMessage {
  assistantMsgCounter += 1;
  return {
    id: `am-${assistantMsgCounter}-${timestamp}`,
    author,
    text,
    timestamp,
    ...(aavaMessageId !== undefined ? { aavaMessageId } : {}),
  };
}

export function chatReducer(state: ChatState, action: ChatAction): ChatState {
  switch (action.type) {
    // =======================================================================
    // UI
    // =======================================================================
    case 'PANEL_OPENED':
      // Bij openen: alles wat er nu staat is "gezien". Markeer de hoogste
      // operator-timestamp. Volgende periode-dicht gaat de teller weer op
      // vanaf hier.
      return {
        ...state,
        ui: {
          ...state.ui,
          panelOpen: true,
          lastReadAt: Math.max(
            state.ui.lastReadAt,
            maxOperatorTimestamp(state.serverMessages),
          ),
        },
      };

    case 'PANEL_CLOSED':
      return { ...state, ui: { ...state.ui, panelOpen: false } };

    case 'PICKER_DRILLDOWN_CHAT':
      return { ...state, ui: { ...state.ui, pickerStep: 'chat-list' } };

    case 'PICKER_BACK_TO_ROOT':
      return { ...state, ui: { ...state.ui, pickerStep: 'root' } };

    case 'CHANNEL_PICKED': {
      // Reset pickerStep zodat een latere terugkeer naar de picker (via
      // back-button of SESSION_RESET) op de gegroepeerde wortelweergave
      // begint, niet in een achtergebleven chat-list sub-state.
      const ui = { ...state.ui, pickedChannel: action.channel, pickerStep: 'root' as const };
      // Repair-request → AVAA-intake auto-starten i.p.v. door de intake-
      // form heen gaan. De AI-assistent neemt het hele gesprek over;
      // er wordt nooit een operator gezocht via dit pad.
      //
      // Triggert wanneer er nog geen flow loopt en de bezoeker niet
      // anoniem-resolved is (anoniem = auth-required-muur). Dekt:
      //   - re-pick na een eerdere webchat-pick (auth='authenticated');
      //   - eerste pick zonder geconfigureerde identityUrl (auth blijft
      //     'unknown' en de controller doet geen identity-check).
      // De parallelle `IDENTITY_RESOLVED_WITH_JWT`-tak overschrijft deze
      // assistant-state alleen wanneer de identity-check daadwerkelijk
      // resolves; tot die tijd zien zowel 'unknown' als 'authenticated'-
      // visitors dezelfde greeting.
      if (
        action.channel === 'repair-request' &&
        state.auth !== 'anonymous' &&
        state.session.phase === 'none' &&
        state.assistant === null
      ) {
        // AVAA-intake: we tonen hier nog GEEN begroeting — die hangt af van
        // het aantal actieve contracten, dat we pas kennen na de fetch. De
        // picker-area toont intussen de loading-skeleton. CONTRACTS_FETCH_-
        // SUCCEEDED kiest vervolgens de juiste intro (1 contract → adres-
        // begroeting + skip picker; meerdere → keuzevraag + picker).
        return {
          ...state,
          ui,
          assistant: { step: 'aava-selecting-contract', collectedName: '' },
          assistantMessages: [],
        };
      }
      // POC: conversational intake voor anoniem webchat. Skip de
      // intake-form en open meteen met een bot-greeting + composer.
      // De eerste user-message wordt afgehandeld door ASSISTANT_USER_REPLIED
      // (asking-question step) → dispatcht naar dezelfde flow als de
      // oude intake-form. Captcha is in deze POC tijdelijk geskipt.
      if (
        action.channel === 'webchat' &&
        state.auth !== 'authenticated' &&
        state.session.phase === 'none' &&
        state.assistant === null
      ) {
        return {
          ...state,
          ui,
          assistant: { step: 'asking-question', collectedName: '' },
          assistantMessages: [
            makeAssistantMessage('assistant', ASSISTANT_COPY.openingPrompt),
          ],
        };
      }
      return { ...state, ui };
    }

    case 'STATE_HYDRATED':
      // Restore-pad uit localStorage. Schrijven naar storage gebeurt NIET
      // hier (zou een no-op write zijn — we lezen er net van).
      return {
        ...state,
        ui: {
          ...state.ui,
          panelOpen: action.panelOpen,
          lastReadAt: action.lastReadAt,
        },
        // Alleen overschrijven als de snapshot 't bevat — undefined
        // betekent: oude snapshot zonder assistantMessages, niet
        // "leeggemaakt".
        assistantMessages:
          action.assistantMessages ?? state.assistantMessages,
      };

    case 'PRE_SESSION_HYDRATED':
      // Pre-session restore: kanaal-keuze + bot-flow + conversation-tot-zover.
      // Wordt alleen gedispatcht door controller wanneer er géén session-
      // snapshot is — anders neemt STATE_HYDRATED/HYDRATE_ACTIVE/HYDRATE_ENDED
      // het over. Schrijft niet terug naar storage (reactToPersistence
      // skipt PRE_SESSION_HYDRATED).
      return {
        ...state,
        ui: {
          ...state.ui,
          pickedChannel: action.pickedChannel,
          pickerStep: action.pickerStep,
        },
        intake: action.intake,
        assistant: action.assistant,
        assistantMessages: action.assistantMessages,
      };

    case 'HYDRATE_ENDED':
      // Volledig herstel van een geëindigd gesprek uit lokale snapshot.
      // Geen polling daarna — het gesprek verandert niet meer en de server
      // kan de sessie al hebben opgeruimd.
      return {
        ...state,
        session: {
          phase: 'ended',
          reason: action.reason,
          operator: action.operator,
          endedAt: action.endedAt,
          id: action.id,
          rating: action.rating,
        },
        serverMessages: action.serverMessages,
        assistantMessages: action.assistantMessages,
        agentTyping: false,
        // Bot-flow is per definitie afgelopen voor een ended-gesprek.
        assistant: null,
      };

    case 'HYDRATE_ACTIVE':
      // Direct herstel naar active-state — operator was al gekoppeld vóór
      // de refresh. Slaat de queued→active-transitie over zodat de
      // bezoeker geen tweede keer de searching-banner ziet voor een
      // gesprek dat al loopt. Polling start vanzelf via reactToPollingSwap
      // (none → active = needsPolling true).
      return {
        ...state,
        session: {
          phase: 'active',
          mode: action.mode,
          id: action.id,
          operator: action.operator,
        },
        serverMessages: action.serverMessages,
        agentTyping: false,
        // Bot-flow is per definitie afgelopen voor een active-gesprek.
        assistant: null,
      };

    // =======================================================================
    // Connection / service-status
    // =======================================================================
    case 'CONNECTION_CHANGED':
      return { ...state, connection: { online: action.online } };

    case 'SERVICE_STATUS_FETCHED':
      return { ...state, serviceStatus: action.status };

    case 'SERVICE_STATUS_FAILED':
      return state;

    // =======================================================================
    // Authenticated flow
    // =======================================================================
    case 'IDENTITY_CHECK_STARTED':
      // Alleen vanaf 'none' starten — nooit een lopende sessie of een al
      // resolvende check overschrijven (race-bescherming bij snelle
      // channel-pick-toggles).
      if (state.session.phase !== 'none') return state;
      return { ...state, session: { phase: 'identifying' } };

    case 'IDENTITY_RESOLVED_WITH_JWT': {
      // JWT parkeren tot intake-submit: POST /authenticated/new heeft een
      // niet-lege initialQuestion nodig (sommige deployments antwoorden
      // anders met 500).
      if (state.session.phase !== 'identifying') return state;
      // Repair-request + authenticated → AI-assistent (AVAA) opent het
      // gesprek met een vrije-vraag-prompt. Geen intake-form, geen
      // captcha, geen bot-Q&A — bezoeker is al geïdentificeerd via het
      // klantportaal en de AI-intake regelt de rest. assistant !== null
      // zorgt dat derivePanelView 'assistant-flow' kiest.
      if (state.ui.pickedChannel === 'repair-request') {
        // Idempotent met de CHANNEL_PICKED-tak: als AVAA al actief is
        // (omdat de pick eerder kwam), behoud de bestaande assistant-state
        // en messages. Anders initialiseren we de selectie-stap MET een lege
        // message-lijst — net als CHANNEL_PICKED. De begroeting hangt af van
        // het aantal contracten en wordt door CONTRACTS_FETCH_SUCCEEDED gezet
        // (1 → adres-begroeting + skip picker; meerdere → keuzevraag). Hier
        // NIET eager `avaaContractPrompt` toevoegen: dat zou de single-contract
        // auto-select blokkeren (guard `assistantMessages.length === 0`).
        const alreadyAava = state.assistant !== null;
        return {
          ...state,
          auth: 'authenticated',
          identityJwt: action.jwt,
          session: { phase: 'none' },
          assistant: alreadyAava
            ? state.assistant
            : { step: 'aava-selecting-contract', collectedName: '' },
          assistantMessages: alreadyAava ? state.assistantMessages : [],
        };
      }
      // Webchat-pad: bezoeker had de vraag al ingediend en stond in
      // `awaiting-identity` te wachten op de identity-resolve. Skip
      // captcha + naam + e-mail en ga direct door naar de authenticated
      // session-request (server identificeert via Bearer-header).
      if (
        state.assistant !== null &&
        state.assistant.step === 'awaiting-identity' &&
        state.assistant.pendingQuestion
      ) {
        return {
          ...state,
          auth: 'authenticated',
          identityJwt: action.jwt,
          intake: {
            question: state.assistant.pendingQuestion,
            captchaToken: '',
            capturedAt: Date.now(),
          },
          session: {
            phase: 'requesting',
            mode: 'authenticated',
            jwt: action.jwt,
          },
          assistant: null,
          assistantMessages: [
            ...state.assistantMessages,
            makeAssistantMessage('assistant', ASSISTANT_COPY.searching),
          ],
        };
      }
      return {
        ...state,
        auth: 'authenticated',
        identityJwt: action.jwt,
        session: { phase: 'none' },
      };
    }

    case 'IDENTITY_RESOLVED_WITHOUT_JWT': {
      // Terug naar 'none'. `auth: 'anonymous'` markeert dat we 't gevraagd
      // hebben zodat selectors voor `repair-request` de auth-required-muur
      // kunnen tonen i.p.v. eindeloos te wachten op identifying.
      if (state.session.phase !== 'identifying') return state;
      // Webchat-pad: bezoeker stond in `awaiting-identity` na het indienen
      // van zijn vraag. Geen JWT → captcha-pad (als Turnstile actief is)
      // of asking-name (of welcomeBack-shortcut voor terugkerende bezoekers).
      if (
        state.assistant !== null &&
        state.assistant.step === 'awaiting-identity' &&
        state.assistant.pendingQuestion
      ) {
        const pendingQuestion = state.assistant.pendingQuestion;
        const collectedName = state.assistant.collectedName;
        const needsCaptcha = !!state.serviceStatus?.turnstile?.siteKey;
        if (needsCaptcha) {
          return {
            ...state,
            auth: 'anonymous',
            session: { phase: 'none' },
            assistant: {
              step: 'awaiting-captcha',
              collectedName,
              pendingQuestion,
            },
            assistantMessages: [
              ...state.assistantMessages,
              makeAssistantMessage('assistant', ASSISTANT_COPY.askCaptcha),
            ],
          };
        }
        const intake = {
          question: pendingQuestion,
          captchaToken: '',
          capturedAt: Date.now(),
        };
        if (state.visitor !== null && state.visitor.email) {
          return {
            ...state,
            auth: 'anonymous',
            session: { phase: 'none' },
            intake,
            assistant: {
              step: 'searching',
              collectedName: state.visitor.firstName,
            },
            assistantMessages: [
              ...state.assistantMessages,
              makeAssistantMessage(
                'assistant',
                ASSISTANT_COPY.welcomeBack(state.visitor.firstName),
              ),
            ],
          };
        }
        return {
          ...state,
          auth: 'anonymous',
          session: { phase: 'none' },
          intake,
          assistant: { step: 'asking-name', collectedName: '' },
          assistantMessages: [
            ...state.assistantMessages,
            makeAssistantMessage('assistant', ASSISTANT_COPY.askNameAfterQuestion),
          ],
        };
      }
      return { ...state, auth: 'anonymous', session: { phase: 'none' } };
    }

    // =======================================================================
    // Intake-form submit → start virtuele assistent
    // =======================================================================
    case 'INTAKE_QUESTION_RECEIVED': {
      // Vraag + token vasthouden tot na de assistent-flow. Echo de vraag
      // direct als customer-balloon zodat de gebruiker zijn eigen input als
      // eerste in de chat ziet — dat is hoe je een gesprek voelt te starten.
      // Zodra de echte sessie loopt mirroret de server hem ook terug; we
      // dedupen die echo in SERVER_STATE_RECEIVED zodat hij maar één keer
      // verschijnt.
      const intake = {
        question: action.question,
        captchaToken: action.captchaToken,
        capturedAt: Date.now(),
      };

      // Authenticated: identity al bekend → skip bot-Q&A + captcha, direct
      // naar 'requesting'. Searching-pill geeft in-stream UI-feedback want
      // de searching-banner zit in de footer.
      if (state.auth === 'authenticated' && state.identityJwt !== null) {
        return {
          ...state,
          intake,
          session: {
            phase: 'requesting',
            mode: 'authenticated',
            jwt: state.identityJwt,
          },
          assistantMessages: [
            makeAssistantMessage('customer', action.question),
            makeAssistantMessage('assistant', ASSISTANT_COPY.searching),
          ],
        };
      }

      // Terugkerende bezoeker: visitor is gehydrateerd uit storage. Skip de
      // hele bot-Q&A en ga direct naar 'searching' — de assistant-search-
      // transitie reactor pakt de timer + INTAKE_SUBMITTED van daar op.
      if (state.visitor !== null && state.visitor.email) {
        return {
          ...state,
          intake,
          assistant: {
            step: 'searching',
            collectedName: state.visitor.firstName,
          },
          assistantMessages: [
            makeAssistantMessage('customer', action.question),
            makeAssistantMessage(
              'assistant',
              ASSISTANT_COPY.welcomeBack(state.visitor.firstName),
            ),
          ],
        };
      }

      // Eerste bezoek: bot vraagt naam → e-mail → searching.
      return {
        ...state,
        intake,
        assistant: { step: 'asking-name', collectedName: '' },
        assistantMessages: [
          makeAssistantMessage('customer', action.question),
          makeAssistantMessage('assistant', ASSISTANT_COPY.greeting),
        ],
      };
    }

    // =======================================================================
    // Virtuele assistent: gebruiker typt antwoord
    // =======================================================================
    // =======================================================================
    // POC conversational intake: Turnstile-token binnen ná awaiting-captcha.
    // Consumeert assistant.pendingQuestion en zet de intake-state. Transitie
    // matches the asking-question → asking-name / welcomeBack pad maar nu
    // mét echte token.
    // =======================================================================
    case 'CAPTCHA_VERIFIED': {
      if (
        state.assistant === null ||
        state.assistant.step !== 'awaiting-captcha' ||
        !state.assistant.pendingQuestion
      ) {
        return state;          // defensief — geen valide pending question
      }
      const intake = {
        question: state.assistant.pendingQuestion,
        captchaToken: action.captchaToken,
        capturedAt: Date.now(),
      };
      // Vink de bestaande captcha-balloon af door de done-suffix erachter
      // te plakken. Geen aparte system-pill — voelt natuurlijker als de
      // oorspronkelijke vraag zelf "klaar" wordt. Idempotent: dubbele
      // CAPTCHA_VERIFIED dispatches (race) plakken de suffix maar één keer.
      const acked = ackCaptchaBalloon(state.assistantMessages);
      // Terugkerende bezoeker → skip naam/email-vragen
      if (state.visitor !== null && state.visitor.email) {
        return {
          ...state,
          intake,
          assistant: {
            step: 'searching',
            collectedName: state.visitor.firstName,
          },
          assistantMessages: [
            ...acked,
            makeAssistantMessage(
              'assistant',
              ASSISTANT_COPY.welcomeBack(state.visitor.firstName),
            ),
          ],
        };
      }
      // Nieuwe bezoeker → vraag naam.
      return {
        ...state,
        intake,
        assistant: { step: 'asking-name', collectedName: '' },
        assistantMessages: [
          ...acked,
          makeAssistantMessage('assistant', ASSISTANT_COPY.askNameAfterQuestion),
        ],
      };
    }

    case 'ASSISTANT_USER_REPLIED': {
      if (state.assistant === null) return state;          // defensief
      const customerMsg = makeAssistantMessage('customer', action.text.trim());

      // POC conversational intake — eerste user-message in asking-question
      // is de eigenlijke vraag. Twee paden:
      //   1. Turnstile actief (serviceStatus.turnstile.siteKey gezet) →
      //      bewaar de vraag in `pendingQuestion`, ga naar `awaiting-captcha`,
      //      injecteer een bot-bericht dat de captcha aankondigt. De UI
      //      rendert dan `<embrace-chat-captcha>` onder de message-list.
      //      CAPTCHA_VERIFIED consumeert de pendingQuestion + token later.
      //   2. Geen Turnstile (stub-mode of klant zonder captcha) → directe
      //      transitie naar asking-name (of welcomeBack-shortcut).
      if (state.assistant.step === 'asking-question') {
        const question = action.text.trim();

        // Authenticated: identity-check is al rond en gaf een JWT terug.
        // Skip captcha + naam/e-mail — server kent de bezoeker via de JWT.
        if (state.auth === 'authenticated' && state.identityJwt !== null) {
          return {
            ...state,
            intake: { question, captchaToken: '', capturedAt: Date.now() },
            session: {
              phase: 'requesting',
              mode: 'authenticated',
              jwt: state.identityJwt,
            },
            assistant: null,
            assistantMessages: [
              ...state.assistantMessages,
              customerMsg,
              makeAssistantMessage('assistant', ASSISTANT_COPY.searching),
            ],
          };
        }

        // Identity-check nog niet binnen (`auth === 'unknown'`): bezoeker
        // typte sneller dan het portal antwoordt. Park de vraag in
        // `pendingQuestion` en wacht — de IDENTITY_RESOLVED_*-paden
        // hieronder beslissen daarna captcha+naam/e-mail vs direct door.
        if (state.auth === 'unknown' && state.session.phase === 'identifying') {
          return {
            ...state,
            assistant: {
              step: 'awaiting-identity',
              collectedName: state.assistant.collectedName,
              pendingQuestion: question,
            },
            assistantMessages: [
              ...state.assistantMessages,
              customerMsg,
              makeAssistantMessage('assistant', ASSISTANT_COPY.checkingIdentity),
            ],
          };
        }

        // Anonymous (geen JWT): captcha-pad als de service Turnstile vereist,
        // anders direct naar naam-vraag of welcomeBack-shortcut.
        const needsCaptcha = !!state.serviceStatus?.turnstile?.siteKey;
        if (needsCaptcha) {
          return {
            ...state,
            assistant: {
              step: 'awaiting-captcha',
              collectedName: state.assistant.collectedName,
              pendingQuestion: question,
            },
            assistantMessages: [
              ...state.assistantMessages,
              customerMsg,
              makeAssistantMessage('assistant', ASSISTANT_COPY.askCaptcha),
            ],
          };
        }
        // Geen captcha nodig — zelfde flow als voorheen.
        const intake = {
          question,
          captchaToken: '',
          capturedAt: Date.now(),
        };
        if (state.visitor !== null && state.visitor.email) {
          return {
            ...state,
            intake,
            assistant: {
              step: 'searching',
              collectedName: state.visitor.firstName,
            },
            assistantMessages: [
              ...state.assistantMessages,
              customerMsg,
              makeAssistantMessage(
                'assistant',
                ASSISTANT_COPY.welcomeBack(state.visitor.firstName),
              ),
            ],
          };
        }
        return {
          ...state,
          intake,
          assistant: { step: 'asking-name', collectedName: '' },
          assistantMessages: [
            ...state.assistantMessages,
            customerMsg,
            makeAssistantMessage('assistant', ASSISTANT_COPY.askNameAfterQuestion),
          ],
        };
      }

      if (state.assistant.step === 'asking-name') {
        const result = validateName(action.text);
        if (!result.ok) {
          return {
            ...state,
            assistantMessages: [
              ...state.assistantMessages,
              customerMsg,
              makeAssistantMessage('assistant', result.error),
            ],
          };
        }
        // Naam ok → advance naar e-mail
        return {
          ...state,
          assistant: { step: 'asking-email', collectedName: result.value },
          assistantMessages: [
            ...state.assistantMessages,
            customerMsg,
            makeAssistantMessage('assistant', ASSISTANT_COPY.thanks(result.value)),
          ],
        };
      }

      if (state.assistant.step === 'asking-email') {
        const result = validateEmail(action.text);
        if (!result.ok) {
          return {
            ...state,
            assistantMessages: [
              ...state.assistantMessages,
              customerMsg,
              makeAssistantMessage('assistant', result.error),
            ],
          };
        }
        // E-mail ok → set visitor, ga naar 'searching' (controller dispatcht
        // de echte INTAKE_SUBMITTED na een korte vertraging zodat het
        // 'searching'-bericht leesbaar is).
        return {
          ...state,
          assistant: { step: 'searching', collectedName: state.assistant.collectedName },
          visitor: {
            firstName: state.assistant.collectedName,
            lastName: '',
            email: result.value,
          },
          assistantMessages: [
            ...state.assistantMessages,
            customerMsg,
            makeAssistantMessage('assistant', ASSISTANT_COPY.searching),
          ],
        };
      }

      if (state.assistant.step === 'aava-asking') {
        // AAVA-intake-conversatie: bezoeker stuurt een bericht. De
        // reducer voegt alleen het customer-message toe; de controller
        // ziet deze actie en triggert daarop de /start- of /message-
        // call. De assistant-reply komt later als AAVA_REPLY_RECEIVED.
        //
        // Bijlagen: stagedFiles worden meegenomen op het customer-msg
        // (alleen metadata — `dataUri` strippen we direct voor memory)
        // en uit staging gewist. De controller leest de dataUris uit
        // `prev.stagedFiles` om ze naar de AAVA-API te sturen.
        const trimmed = action.text.trim();
        // Lege tekst is acceptabel zolang er bijlagen zijn — een
        // foto-only-bericht moet ook kunnen ("hier is de foto").
        if (trimmed.length === 0 && state.stagedFiles.length === 0) {
          return state;
        }
        // We bewaren `dataUri` voor `image`-bestanden zodat de
        // customer-bubble een inline-thumb kan tonen (visueel bewijs
        // dat de bezoeker zijn foto heeft verzonden). Voor non-image-
        // files (pdf, generieke) strippen we 'm — daar voegt 'ie niets
        // toe voor preview en kost alleen geheugen.
        const fileMeta = state.stagedFiles.map((f) =>
          f.kind === 'image' && f.dataUri
            ? {
                id: f.id,
                name: f.name,
                sizeLabel: f.sizeLabel,
                kind: f.kind,
                dataUri: f.dataUri,
              }
            : {
                id: f.id,
                name: f.name,
                sizeLabel: f.sizeLabel,
                kind: f.kind,
              },
        );
        const customerMsg = makeAssistantMessage('customer', trimmed);
        return {
          ...state,
          stagedFiles: [],
          assistantMessages: [
            ...state.assistantMessages,
            fileMeta.length > 0 ? { ...customerMsg, files: fileMeta } : customerMsg,
          ],
        };
      }

      // step === 'searching' → handleUserSubmit zou hier niet meer mogen
      // dispatchen. Defensief no-op-pad voor race-dispatches.
      return state;
    }

    // =======================================================================
    // Echte session-request (na assistent-flow OF rechtstreeks)
    // =======================================================================
    case 'INTAKE_SUBMITTED': {
      const visitor =
        action.visitor ??
        state.visitor ?? {
          firstName: '',
          lastName: '',
          email: '',
        };
      // Geen pendingMessage meer — de question gaat als initialQuestion
      // naar de server, die hem terug-spiegelt in serverMessages.
      // De assistent-conversatie blijft staan voor continuiteit.
      return {
        ...state,
        visitor,
        session: { phase: 'requesting', mode: 'anonymous' },
      };
    }

    // =======================================================================
    // Session lifecycle
    // =======================================================================
    case 'SESSION_STARTED':
      // Na session start gaan we eerst naar 'queued' (wachtrij).
      // Transitie naar 'active' gebeurt via SESSION_AGENT_ATTACHED.
      // assistant=null: bot-flow is officieel afgelopen, ASSISTANT_USER_REPLIED
      // wordt vanaf nu een no-op. Voorkomt dat visitor-input per ongeluk in
      // de bot-state belandt i.p.v. naar de server gaat.
      // identityJwt=null: kccConversationRequestId is vanaf nu het anker
      // (C1-gedrag, zie types.ts).
      return {
        ...state,
        identityJwt: null,
        session: {
          phase: 'queued',
          mode: action.mode,
          id: action.id,
          queueInfo: null,
        },
        assistant: null,
      };

    case 'ASSISTANT_OPERATOR_FOUND': {
      // Nog steeds in queued: searching-banner blijft, bezoeker leest de
      // bot-aankondiging. Pas SESSION_AGENT_ATTACHED switcht naar 'active'.
      // Idempotent: bij een refresh tijdens deze tussenfase staat de
      // message al in de gepersisteerde snapshot.
      if (state.session.phase !== 'queued') return state;
      const announceText = i18n.bot.operatorFound(action.operator.name);
      const alreadyAnnounced = state.assistantMessages.some(
        (m) => m.author === 'assistant' && m.text === announceText,
      );
      if (alreadyAnnounced) return state;
      return {
        ...state,
        assistantMessages: [
          ...state.assistantMessages,
          makeAssistantMessage('assistant', announceText, action.at),
        ],
      };
    }

    case 'SESSION_AGENT_ATTACHED': {
      if (state.session.phase !== 'queued') return state;
      // Plaats een grijze system-pill in de chat-stream zodat de bezoeker
      // expliciet de transitie ziet ("ik zoek nu een medewerker..." → bam,
      // operator gekoppeld). Sluit aan op het vorige assistent-bericht.
      //
      // Idempotent: na een refresh tijdens active session ziet de
      // bootstrap eerst phase='queued' (gehydrateerd) en triggert dan
      // opnieuw deze attach-action via polling. assistantMessages bevat
      // dan al de pill uit de gepersisteerde snapshot — geen duplicaat
      // toevoegen.
      const pillText = i18n.bot.operatorAttachedSystem(action.operator.name);
      const alreadyHasPill = state.assistantMessages.some(
        (m) => m.author === 'system' && m.text === pillText,
      );
      return {
        ...state,
        session: {
          phase: 'active',
          mode: state.session.mode,
          id: state.session.id,
          operator: action.operator,
        },
        assistantMessages: alreadyHasPill
          ? state.assistantMessages
          : [
              ...state.assistantMessages,
              makeAssistantMessage('system', pillText, action.at),
            ],
      };
    }

    case 'QUEUE_INFO_UPDATED': {
      if (state.session.phase !== 'queued') return state;
      return {
        ...state,
        session: { ...state.session, queueInfo: action.queueInfo },
      };
    }

    case 'SESSION_REQUEST_FAILED': {
      // De bezoeker mag zijn chat-context niet kwijtraken na een
      // mislukte session-start. Twee paden:
      //   1. Bot-flow liep (assistant !== null): houd de stream aan,
      //      voeg een system-pill toe met de reden, en zet step terug
      //      naar 'asking-question' met de oorspronkelijke vraag in
      //      `pendingQuestion`. Composer komt terug enabled zodat de
      //      bezoeker direct opnieuw kan indienen. `intake` wist zodat
      //      reactToAssistantSearchTransition een schone retry kan doen.
      //   2. Geen bot-flow (legacy intake-form-pad): default-tak, alleen
      //      de phase-overgang zodat de form een retry-knop kan tonen.
      const errorText = sessionFailureCopy(action.reason);
      if (state.assistant !== null) {
        const previousQuestion = state.intake?.question ?? '';
        return {
          ...state,
          session: {
            phase: 'error',
            reason: action.reason,
            recoverable: action.recoverable,
          },
          intake: null,
          assistant: {
            step: 'asking-question',
            collectedName: state.assistant.collectedName,
            ...(previousQuestion ? { pendingQuestion: previousQuestion } : {}),
          },
          assistantMessages: [
            ...state.assistantMessages,
            makeAssistantMessage('system', errorText),
          ],
        };
      }
      return {
        ...state,
        session: {
          phase: 'error',
          reason: action.reason,
          recoverable: action.recoverable,
        },
      };
    }

    case 'SESSION_ENDED': {
      const operator =
        state.session.phase === 'active'
          ? state.session.operator
          : null;
      // Bewaar de session-id zodat een latere POST /rate (na refresh
      // of zelfs nadat de bezoeker meerdere minuten in de ended-state
      // zat) nog kan plaatsvinden.
      const id =
        state.session.phase === 'active' || state.session.phase === 'queued'
          ? state.session.id
          : null;
      // Rating-flow alleen aanbieden als (1) operator daadwerkelijk meedeed
      // én (2) we de session-id konden bewaren — anders zou de UI in
      // success-state komen maar de POST stilletjes droppen omdat
      // reactToRatingSubmission geen target heeft. Beide criteria gelden
      // voor sessies die via 'active' liepen.
      const rating: RatingState | null =
        operator !== null && id !== null ? { status: 'idle' } : null;
      // Als de server een uitleg meegeeft (bv. "geen medewerker beschikbaar"),
      // plaats hem als grijze pill in de chat-stream zodat de bezoeker ziet
      // waarom het gesprek eindigde.
      const assistantMessages = action.systemMessage
        ? [
            ...state.assistantMessages,
            makeAssistantMessage('system', action.systemMessage),
          ]
        : state.assistantMessages;
      return {
        ...state,
        session: {
          phase: 'ended',
          reason: action.reason,
          operator,
          endedAt: Date.now(),
          id,
          rating,
        },
        agentTyping: false,
        assistantMessages,
      };
    }

    case 'SESSION_LOST':
      return {
        ...state,
        session: {
          phase: 'error',
          reason: 'session_expired',
          recoverable: true,
        },
      };

    case 'SESSION_RESET':
      return {
        ...state,
        // pickedChannel ook wissen zodat multi-channel bezoekers na
        // "Nieuw gesprek" opnieuw kanaal kunnen kiezen. pickerStep
        // terug naar 'root' zodat ze niet midden in een chat-drilldown
        // landen.
        ui: { ...state.ui, pickedChannel: null, pickerStep: 'root' },
        // Auth-status terug naar 'unknown' — bezoeker kan tussendoor
        // ingelogd zijn op een ander tabblad of een sessie hebben laten
        // verlopen. Volgende channel-pick triggert dan opnieuw een check.
        auth: 'unknown',
        identityJwt: null,
        session: { phase: 'none' },
        visitor: null,
        agentTyping: false,
        serverMessages: [],
        pendingMessages: [],
        stagedFiles: [],
        intake: null,
        assistant: null,
        assistantMessages: [],
        contracts: { status: 'idle', items: [], selectedId: null, error: null },
        aava: {
          status: 'idle',
          conversationId: null,
          error: null,
          finished: false,
          outcome: null,
          selfFix: 'idle',
        },
      };

    // =======================================================================
    // Typing
    // =======================================================================
    case 'AGENT_TYPING_CHANGED':
      if (state.agentTyping === action.typing) return state;
      return { ...state, agentTyping: action.typing };

    // =======================================================================
    // Messages
    // =======================================================================
    case 'MESSAGE_QUEUED':
      return {
        ...state,
        pendingMessages: [...state.pendingMessages, action.message],
        stagedFiles: [],
      };

    case 'MESSAGE_POST_SUCCEEDED':
      return {
        ...state,
        pendingMessages: state.pendingMessages.map((m) =>
          m.localId === action.localId ? { ...m, status: 'awaiting' } : m,
        ),
      };

    case 'MESSAGE_POST_FAILED':
      return {
        ...state,
        pendingMessages: state.pendingMessages.map((m) =>
          m.localId === action.localId ? { ...m, status: 'failed' } : m,
        ),
      };

    case 'MESSAGE_RETRY_REQUESTED':
      return {
        ...state,
        pendingMessages: state.pendingMessages.map((m) =>
          m.localId === action.localId ? { ...m, status: 'sending' } : m,
        ),
      };

    case 'SERVER_STATE_RECEIVED': {
      // Als panel open is, ziet de bezoeker wat binnenkomt direct → markeer
      // het meteen als gelezen. Dicht: laat lastReadAt staan zodat het
      // unread-aantal oploopt.
      const newLastReadAt = state.ui.panelOpen
        ? Math.max(
            state.ui.lastReadAt,
            maxOperatorTimestamp(action.messages),
          )
        : state.ui.lastReadAt;
      // Filter de server-echo van de initiële vraag eruit — die staat al
      // als customer-balloon in assistantMessages (zie INTAKE_QUESTION_RECEIVED).
      const filteredServer = removeAssistantEcho(
        action.messages,
        state.assistantMessages,
      );
      // Polling vuurt elke ~2s; vaak is de server-snapshot identiek aan de
      // vorige. Behoud dezelfde array-referentie zodat downstream-watchers
      // (controller schrijft active-snapshot bij serverMessages-mutatie)
      // niet onnodig getriggerd worden.
      const nextServer = sameServerMessages(state.serverMessages, filteredServer)
        ? state.serverMessages
        : filteredServer;
      const nextPending = removeDuplicatedPending(
        state.pendingMessages,
        action.messages,
      );
      return {
        ...state,
        serverMessages: nextServer,
        pendingMessages: nextPending,
        ui: { ...state.ui, lastReadAt: newLastReadAt },
      };
    }

    // =======================================================================
    // Staged files
    // =======================================================================
    case 'FILES_STAGED':
      return {
        ...state,
        stagedFiles: [...state.stagedFiles, ...action.files],
      };

    case 'STAGED_FILE_REMOVED':
      return {
        ...state,
        stagedFiles: state.stagedFiles.filter((f) => f.id !== action.fileId),
      };

    case 'STAGED_FILES_CLEARED':
      return { ...state, stagedFiles: [] };

    // =======================================================================
    // Contract-selectie (AVAA-intake)
    // =======================================================================
    case 'CONTRACTS_FETCH_STARTED':
      // Idempotent: dubbele dispatches (bv. re-mount na refresh) houden de
      // status op 'loading' i.p.v. een eventueel `selectedId` te wissen
      // dat al was gekozen. Items leegmaken is veilig — de fetch komt eraan.
      return {
        ...state,
        contracts: {
          status: 'loading',
          items: state.contracts.items,
          selectedId: state.contracts.selectedId,
          error: null,
        },
      };

    case 'CONTRACTS_FETCH_SUCCEEDED': {
      const items = action.items;
      // Behoud een eerdere selectie als het contract nog in de nieuwe lijst
      // staat — refresh mag de keuze niet kosten. Anders → null (stale).
      const keptSelectedId =
        state.contracts.selectedId &&
        items.some((c) => c.id === state.contracts.selectedId)
          ? state.contracts.selectedId
          : null;
      const loaded = {
        status: 'loaded' as const,
        items,
        selectedId: keptSelectedId,
        error: null,
      };

      // Eerste keer dat de lijst binnenkomt tijdens contract-selectie kiezen
      // we de begroeting op basis van het aantal actieve contracten (de /aava
      // proxy levert al uitsluitend actieve). `assistantMessages.length === 0`
      // garandeert dat een eventuele re-fetch de intro niet dupliceert.
      if (
        state.assistant?.step === 'aava-selecting-contract' &&
        state.assistantMessages.length === 0
      ) {
        // Eén contract → sla de picker over, selecteer automatisch en
        // begroet meteen met het adres; door naar 'aava-asking'.
        if (items.length === 1) {
          const only = items[0]!;
          return {
            ...state,
            contracts: { ...loaded, selectedId: only.id },
            assistant: { ...state.assistant, step: 'aava-asking' },
            assistantMessages: [
              makeAssistantMessage(
                'assistant',
                ASSISTANT_COPY.avaaSingleGreeting(formatUnitAddress(only)),
              ),
            ],
          };
        }
        // Meerdere contracten → toon de keuzevraag; de picker rendert de
        // contracten. De vervolgprompt komt bij CONTRACT_SELECTED.
        if (items.length > 1) {
          return {
            ...state,
            contracts: loaded,
            assistantMessages: [
              makeAssistantMessage('assistant', ASSISTANT_COPY.avaaContractPrompt),
            ],
          };
        }
        // 0 contracten → geen begroeting; de picker toont de empty-state.
      }

      return { ...state, contracts: loaded };
    }

    case 'CONTRACTS_FETCH_FAILED':
      return {
        ...state,
        contracts: {
          status: 'error',
          items: state.contracts.items,
          selectedId: state.contracts.selectedId,
          error: action.reason,
        },
      };

    case 'CONTRACT_SELECTED': {
      // Alleen geldig als het contract daadwerkelijk in de geladen lijst zit
      // — voorkomt een race waar de UI een oude selectie probeert te
      // restoren na CONTRACTS_FETCH_SUCCEEDED met een nieuwe set.
      const chosen = state.contracts.items.find(
        (c) => c.id === action.contractId,
      );
      if (!chosen) return state;
      const selectedChanged = state.contracts.selectedId !== action.contractId;
      const contracts = selectedChanged
        ? { ...state.contracts, selectedId: action.contractId }
        : state.contracts;

      // In de inline-flow ('aava-selecting-contract') is de klik óók
      // het antwoord op de bot-prompt — we voegen een customer-message
      // toe met het gekozen adres + de vervolgvraag van de assistent,
      // en schuiven door naar 'aava-asking'. Andere AVAA-stappen (re-
      // selectie na een eerste keuze) muteren alleen de selectedId.
      if (state.assistant?.step === 'aava-selecting-contract') {
        const address = formatUnitAddress(chosen);
        return {
          ...state,
          contracts,
          assistant: { ...state.assistant, step: 'aava-asking' },
          assistantMessages: [
            ...state.assistantMessages,
            makeAssistantMessage('customer', address),
            makeAssistantMessage('assistant', ASSISTANT_COPY.avaaGreeting),
          ],
        };
      }

      if (!selectedChanged) return state;
      return { ...state, contracts };
    }

    // =======================================================================
    // AAVA-intake-conversatie
    // =======================================================================
    case 'AAVA_REQUEST_STARTED':
      return {
        ...state,
        aava: {
          ...state.aava,
          // 'starting' = eerste call (incl. /start), 'sending' = vervolg
          status: state.aava.conversationId === null ? 'starting' : 'sending',
          error: null,
        },
      };

    case 'AAVA_CONVERSATION_CREATED':
      // Tussenstap: /start is gelukt, /message volgt nog. Status blijft
      // 'starting' tot AAVA_REPLY_RECEIVED of AAVA_REQUEST_FAILED.
      return {
        ...state,
        aava: { ...state.aava, conversationId: action.conversationId },
      };

    case 'AAVA_REPLY_RECEIVED':
      // Reply binnen → voeg toe als assistant-message (krijgt automatisch
      // de 800ms typing-reveal van message-list) en zet status terug op
      // idle zodat composer ontgrendelt.
      //
      // We laten de data-URIs van eerdere customer-foto's bewust intact:
      // ze blijven zichtbaar in de chat tijdens de sessie. Stripping
      // gebeurt alléén bij quota-overflow in `saveAavaSession`
      // (storage.ts) — daar telt quota wel, in-memory niet
      // noemenswaardig.
      return {
        ...state,
        aava: { ...state.aava, status: 'idle', error: null },
        assistantMessages: [
          ...state.assistantMessages,
          makeAssistantMessage(
            'assistant',
            action.text,
            Date.now(),
            action.messageId ?? undefined,
          ),
        ],
      };

    case 'AAVA_REQUEST_FAILED':
      // Foutpad. ConversationId behouden zodat een retry alleen /message
      // doet en niet opnieuw /start. UI mag hier een retry-knop tonen
      // (toekomstig) of een system-pill met de error-reason.
      return {
        ...state,
        aava: { ...state.aava, status: 'error', error: action.reason },
      };

    case 'AAVA_CONVERSATION_FINISHED': {
      // Server heeft `finishedAt` gezet → intake is afgerond. Idempotent:
      // de poller kan 'm nog een keer zien voordat de vervolg-actie vuurt.
      if (state.aava.finished || state.aava.selfFix === 'asking') return state;
      // `Resolved` = tijdens de chat opgelost (self-fix): stel de ja/nee-vraag
      // i.p.v. de `chat-voltooid`-redirect. `finished` blijft `false` zodat de
      // controller geen redirect triggert. Overige outcomes (of geen
      // herkenbare outcome) → bestaande flow: composer dimmen + slotbericht,
      // controller redirect naar `chat-voltooid`.
      if (action.outcome === 'Resolved') {
        return {
          ...state,
          aava: { ...state.aava, status: 'idle', error: null, outcome: 'Resolved', selfFix: 'asking' },
        };
      }
      return {
        ...state,
        aava: {
          ...state.aava,
          status: 'idle',
          error: null,
          finished: true,
          outcome: action.outcome,
        },
        assistantMessages: [
          ...state.assistantMessages,
          makeAssistantMessage('assistant', i18n.aava.completed),
        ],
      };
    }

    case 'AAVA_SELF_FIX_CONFIRMED':
      // "Ja, opgelost" — niks server-side; de backend heeft `Resolved` al
      // vastgelegd. Alleen de vraag opruimen; de controller sluit het paneel.
      if (state.aava.selfFix !== 'asking') return state;
      return {
        ...state,
        aava: { ...state.aava, selfFix: 'idle' },
      };

    case 'AAVA_SELF_FIX_DECLINED':
      // "Nee, niet opgelost" — excuusbericht tonen; de controller stuurt kort
      // daarna door naar de reparatieverzoek-pagina.
      if (state.aava.selfFix !== 'asking') return state;
      return {
        ...state,
        aava: { ...state.aava, selfFix: 'idle' },
        assistantMessages: [
          ...state.assistantMessages,
          makeAssistantMessage('assistant', i18n.aava.selfFix.apology),
        ],
      };

    case 'AAVA_SESSION_HYDRATED':
      // Page-refresh herstel: zet alle benodigde state in één klap zodat
      // we direct in 'aava-asking' belanden. `phase: 'identifying'` zodat
      // de daaropvolgende IDENTITY_CHECK + IDENTITY_RESOLVED_*-flow z'n
      // werk kan doen (guards op die acties verwachten 'identifying').
      // `aava.status: 'starting'` zorgt voor de bot-typing-indicator
      // tijdens het ophalen van de transcript.
      return {
        ...state,
        ui: { ...state.ui, pickedChannel: 'repair-request' },
        session: { phase: 'identifying' },
        assistant: { step: 'aava-asking', collectedName: '' },
        assistantMessages: [],
        contracts: {
          ...state.contracts,
          selectedId: action.contractId,
        },
        aava: {
          status: 'starting',
          conversationId: action.conversationId,
          error: null,
          finished: false,
          outcome: null,
          selfFix: 'idle',
        },
      };

    case 'AAVA_MESSAGES_HYDRATED':
      // Transcript binnen: vervang assistantMessages, status weer idle
      // zodat composer ontgrendelt.
      return {
        ...state,
        aava: { ...state.aava, status: 'idle', error: null },
        assistantMessages: action.messages,
      };

    case 'AAVA_MESSAGES_RECONCILED': {
      // Poll-vangnet: voeg alléén assistant-replies toe die we nog niet
      // tonen. Dedup op `aavaMessageId` tegen de al-gerenderde berichten,
      // zodat een synchroon getoonde reply (zelfde messageId) niet dubbel
      // verschijnt. Nieuwe replies sorteren we op messageId en hangen we
      // achteraan — de realistische gemiste-reply is de laatste.
      const seen = new Set<number>();
      for (const m of state.assistantMessages) {
        if (m.aavaMessageId !== undefined) seen.add(m.aavaMessageId);
      }
      const fresh = action.messages
        .filter((m) => !seen.has(m.messageId))
        .sort((a, b) => a.messageId - b.messageId);
      if (fresh.length === 0) return state; // niets nieuws → referentieel gelijk
      return {
        ...state,
        assistantMessages: [
          ...state.assistantMessages,
          ...fresh.map((m) =>
            makeAssistantMessage('assistant', m.text, m.timestamp, m.messageId),
          ),
        ],
      };
    }

    case 'RATING_SUBMITTED': {
      if (state.session.phase !== 'ended' || state.session.rating === null) {
        return state;
      }
      // Idempotent: alleen vanuit idle naar submitted. Andere transities
      // (al-submitted, al-dismissed) zijn no-ops.
      if (state.session.rating.status !== 'idle') return state;
      // Defense-in-depth range-check. UI clampt al naar 1..5, maar de
      // action-type laat technisch elk number toe; wij weigeren rommel
      // i.p.v. 'm te POSTen.
      if (
        !Number.isInteger(action.rating) ||
        action.rating < 1 ||
        action.rating > 5
      ) {
        return state;
      }
      return {
        ...state,
        session: {
          ...state.session,
          rating: { status: 'submitted', rating: action.rating },
        },
      };
    }

    case 'RATING_DISMISSED': {
      if (state.session.phase !== 'ended' || state.session.rating === null) {
        return state;
      }
      if (state.session.rating.status !== 'idle') return state;
      return {
        ...state,
        session: { ...state.session, rating: { status: 'dismissed' } },
      };
    }

    case 'RATING_HYDRATED': {
      // Cross-tab sync: andere tab heeft de rating-state geüpdatet,
      // wij brengen alleen de waarden in lijn — geen side-effects.
      if (state.session.phase !== 'ended' || state.session.rating === null) {
        return state;
      }
      if (ratingEqual(action.rating, state.session.rating)) return state;
      // No-downgrade rule: een lokaal terminal state (submitted/dismissed)
      // is de bezoeker's eigen committed actie en mag NIET overschreven
      // worden door een racende andere tab. Race-voorbeeld: tab A submit,
      // tab B (nog idle) klikt "Niet nu" tegelijk → B's storage-write
      // arriveert in A en zou A's success-card naar dismissed kunnen
      // overschrijven. Negeer 'm.
      if (state.session.rating.status !== 'idle') return state;
      return {
        ...state,
        session: { ...state.session, rating: action.rating },
      };
    }

    case 'VISITOR_HYDRATED': {
      // Cross-tab of bootstrap. Alleen relevant in 'none'-phase: tijdens
      // een lopende sessie heeft state.visitor al een waarde uit het
      // origineel-INTAKE_SUBMITTED-pad en willen we 'm niet onverhoeds
      // overschrijven. Anders → no-op zodat sessies stabiel blijven.
      if (state.session.phase !== 'none') return state;
      const next = { firstName: action.firstName, lastName: '-', email: action.email };
      if (
        state.visitor !== null &&
        state.visitor.firstName === next.firstName &&
        state.visitor.email === next.email
      ) {
        return state;
      }
      return { ...state, visitor: next };
    }

    case 'VISITOR_FORGOTTEN': {
      // Side-effect (clear storage + cascade SESSION_RESET) gebeurt in
      // de controller. Hier alleen de in-memory state opschonen.
      return { ...state, visitor: null };
    }

    default:
      // assertNever throwt — returnpad bestaat niet meer maar maakt
      // het type-systeem blij dat we exhaustief zijn.
      return assertNever(action);
  }
}

function removeDuplicatedPending(
  pending: PendingMessage[],
  server: ServerMessage[],
): PendingMessage[] {
  const matchedServerIds = new Set<string>();
  const remaining: PendingMessage[] = [];
  let removed = 0;

  for (const p of pending) {
    if (p.status === 'failed') {
      remaining.push(p);
      continue;
    }

    const match = server.find(
      (s) =>
        s.author === 'customer' &&
        s.text === p.text &&
        Math.abs(s.timestamp - p.timestamp) < DEDUP_WINDOW_MS &&
        !matchedServerIds.has(s.id),
    );

    if (match) {
      matchedServerIds.add(match.id);
      removed++;
    } else {
      remaining.push(p);
    }
  }

  // Stable reference when nothing was dedupped — avoids spurious snapshot
  // writes downstream (controller persists active-snapshot whenever
  // serverMessages OR pendingMessages reference changes per poll-tick).
  return removed === 0 ? pending : remaining;
}

/**
 * Verwijdert de eerste customer-server-message die overeenkomt met de
 * EERSTE customer-echo in assistantMessages (de initiële intake-vraag —
 * zie INTAKE_QUESTION_RECEIVED). Voorkomt dat de gebruiker zijn vraag
 * tweemaal ziet zodra de server hem terug-mirrort.
 *
 * Strikt: alleen de eerste echo wordt overwogen, niet alle customer-echoes
 * (naam/e-mail) — anders zou een latere chat-message die toevallig dezelfde
 * tekst bevat als de naam/e-mail van de bezoeker ook gefilterd worden.
 *
 * Match-criterium: gelijke tekst + timestamps binnen ASSISTANT_ECHO_WINDOW_MS
 * (~5 min, sluit aan op Turnstile-token-TTL).
 */
function removeAssistantEcho(
  server: ServerMessage[],
  assistant: AssistantMessage[],
): ServerMessage[] {
  // Alleen het eerste customer-bericht (de intake-vraag) is een potentiële
  // server-echo. Naam/e-mail uit de bot-Q&A bereiken de server niet.
  const intakeQuestionEcho = assistant.find((a) => a.author === 'customer');
  if (!intakeQuestionEcho) return server;

  const echoIdx = server.findIndex(
    (s) =>
      s.author === 'customer' &&
      s.text === intakeQuestionEcho.text &&
      Math.abs(s.timestamp - intakeQuestionEcho.timestamp) <
        ASSISTANT_ECHO_WINDOW_MS,
  );
  // Geen echo gevonden → originele referentie teruggeven. Belangrijk voor
  // de controller: SERVER_STATE_RECEIVED vuurt elke poll-tick (~2s), en
  // een nieuwe array zou ten onrechte een active-snapshot-rewrite triggeren.
  if (echoIdx === -1) return server;
  return [...server.slice(0, echoIdx), ...server.slice(echoIdx + 1)];
}

/**
 * Inhoudelijke gelijkheid voor de poll-snapshot: zelfde lengte + per index
 * dezelfde id, timestamp en text. Genoeg om onveranderde polls te
 * detecteren — `id` is stabiel en `timestamp`+`text` vangen edits af.
 */
function sameServerMessages(
  prev: ServerMessage[],
  next: ServerMessage[],
): boolean {
  if (prev === next) return true;
  if (prev.length !== next.length) return false;
  for (let i = 0; i < prev.length; i++) {
    const a = prev[i]!;
    const b = next[i]!;
    if (a.id !== b.id || a.timestamp !== b.timestamp || a.text !== b.text) {
      return false;
    }
  }
  return true;
}

function maxOperatorTimestamp(messages: ServerMessage[]): number {
  let max = 0;
  for (const m of messages) {
    if (m.author === 'operator' && m.timestamp > max) max = m.timestamp;
  }
  return max;
}

function assertNever(action: never): never {
  // TypeScript exhaustive-check vangnet. Throwen i.p.v. silent return zodat
  // een vergeten action-type direct opvalt in development én productie.
  throw new Error(
    `Unhandled action type: ${(action as { type?: string })?.type ?? '<unknown>'}`,
  );
}

function ratingEqual(a: RatingState, b: RatingState): boolean {
  if (a.status !== b.status) return false;
  if (a.status === 'submitted' && b.status === 'submitted') {
    return a.rating === b.rating;
  }
  return true;
}

